'use client';

import Link from 'next/link';
import { useState } from 'react';

export default function WatchControls({
  seriesSlug,
  episodeId,
  episodeNumber,
  seriesTitle,
  isWatched: initialWatched,
  prevHref,
  nextHref,
  hasNext,
}: {
  seriesSlug: string;
  episodeId: number;
  episodeNumber: number;
  seriesTitle: string;
  isWatched: boolean;
  prevHref: string | null;
  nextHref: string | null;
  hasNext: boolean;
}) {
  const [watched, setWatched] = useState(initialWatched);
  const [busy, setBusy] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);

  const markWatched = async () => {
    setBusy(true);
    const me = await fetch('/api/auth/me').then((r) => r.json()).catch(() => ({ user: null }));
    if (!me.user) {
      setNeedsAuth(true);
      setBusy(false);
      return;
    }
    const next = !watched;
    await fetch('/api/watch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ episodeId, watched: next }),
    });
    setWatched(next);
    setBusy(false);
  };

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        {prevHref && (
          <Link href={prevHref} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm font-medium">
            ← Ep {prevHref.split('/').pop()}
          </Link>
        )}
        <span className="flex-1 text-center font-semibold text-lg truncate">
          {seriesTitle} — Ep {episodeNumber}
        </span>
        {nextHref && (
          <Link href={nextHref} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm font-medium">
            Ep {nextHref.split('/').pop()} →
          </Link>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button
          onClick={markWatched}
          disabled={busy}
          className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
            watched
              ? 'bg-emerald-600/20 text-emerald-300 border-emerald-600/50'
              : 'bg-zinc-800 text-zinc-200 border-zinc-700 hover:bg-zinc-700'
          }`}
        >
          {watched ? '✓ Watched' : 'Mark as watched'}
        </button>
        {hasNext && watched && (
          <Link
            href={nextHref!}
            className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold"
          >
            Next episode →
          </Link>
        )}
        {needsAuth && (
          <Link href={`/login?next=/watch/${seriesSlug}/${episodeNumber}`} className="text-sm text-violet-400">
            Log in to track progress →
          </Link>
        )}
      </div>
    </div>
  );
}
